/**
 * THE SIGNED-IN SHOP'S CLOCK, WITHOUT ASKING AGAIN.
 *
 * src/domain/shopday.ts holds the calendar arithmetic and stays pure — the
 * zone is an argument on every function, because the server imports that
 * file and serves a different shop every few seconds. This is the UI's
 * copy of the answer: primed once at boot from the account whoAmI() already
 * loaded, and read synchronously everywhere after.
 *
 * WHY SYNCHRONOUS MATTERS. This is the same argument src/ui/words.ts makes
 * for "vehicle or door", and it applies harder here. Nearly every render
 * function in this app builds a template literal and returns a string.
 * Making them await a timezone lookup would mean making them async, and
 * that spreads: the schedule grid, the day map, every row builder. One
 * await at boot is the whole cost.
 *
 * BEFORE PRIMING IT IS PACIFIC. Deliberate, not an empty state to guard: a
 * screen drawn before boot finished would otherwise have no zone at all,
 * and Pacific is what every shop's screens used before this existed. The
 * worst case is the old bug for one paint, rather than a blank board.
 *
 * WHY THE SCREENS AND THE SEND WINDOW ARE PRIMED SEPARATELY. The server
 * reads accounts.timezone through server/desk/zone.ts, which caches for a
 * minute and keys by account. This one holds a single shop because a
 * browser session only ever has one. Same column, same fallback, two
 * lifetimes — and neither can be used from the other's side.
 */

import {
  dayIn, dayKeyIn, dayStartIsoIn, startOfDayIn, timeIn, key,
} from "../domain/shopday.ts";
import { PACIFIC, usableZone, readBookingTime } from "../domain/zoneclock.ts";

let zone: string = PACIFIC;

/** Called once at boot, with the signed-in shop's timezone. */
export function primeZone(tz: string | null | undefined): void {
  zone = usableZone(tz);
}

/**
 * The shop's timezone, for the few screens that hand it to Intl directly —
 * a weekday heading, a month label. Prefer the bound helpers below.
 */
export const shopZone = (): string => zone;

/** "2026-09-03" from a calendar day. Zone-free; re-exported so the screens
 *  have one import for the whole clock. */
export { key };

/** The calendar day an instant falls on, in the shop's zone. */
export const shopDay = (at: Date): Date => dayIn(zone, at);

/** The key of the day an instant falls on, in the shop's zone. */
export const shopDayKey = (at: Date): string => dayKeyIn(zone, at);

/** The moment the shop's midnight begins on that calendar day. */
export const startOfShopDay = (cal: Date): Date => startOfDayIn(zone, cal);

/** The instant the shop's day started, as an ISO string. */
export const shopDayStartIso = (at = new Date()): string => dayStartIsoIn(zone, at);

/** The time of day, in the shop's zone. "9:30", or "no time". */
export const shopTime = (iso: string | null): string => timeIn(zone, iso);

/**
 * A DAY AND A TIME YOU COULD READ OUT IN A DISPUTE.
 *
 * "3 days ago" is fine for reading the shape of a job and useless when
 * somebody is holding a bank statement asking when a payment landed. A
 * relative age cannot be checked against anything; this can. In the
 * shop's zone, so money taken at 5pm never reads as the next day.
 *
 * `withTime` off gives the day alone, for lists where the minute is noise.
 */
export function shopStamp(iso: string | null | undefined, withTime = true): string {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  return new Date(t).toLocaleString("en-US", {
    timeZone: zone, weekday: "short", month: "short", day: "numeric",
    ...(withTime ? { hour: "numeric" as const, minute: "2-digit" as const } : {}),
  });
}

/**
 * A BOOKING BOX IN THE SHOP'S CLOCK, BOTH WAYS.
 *
 * <input type="date">, "time" and "datetime-local" carry a wall clock with
 * no zone on it. They used to be filled and read in the BROWSER's zone, so
 * Anthony in California opening a Wrench job in Albuquerque saw 11:00 in
 * the box for a noon appointment, and a time he typed was saved an hour
 * out. These two keep the box on the shop's clock, whoever is looking.
 */
export function shopWallValue(iso: string | null | undefined): string {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  const p: Record<string, string> = {};
  for (const part of new Intl.DateTimeFormat("en-CA", {
    timeZone: zone, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  }).formatToParts(new Date(t))) p[part.type] = part.value;
  return `${p["year"]}-${p["month"]}-${p["day"]}T${p["hour"]}:${p["minute"]}`;
}

/** The instant a booking box means, read on the shop's clock. Null if unreadable. */
export const fromShopWall = (value: string): Date | null => readBookingTime(zone, value);
